import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { animate, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import svg from "../../utils/svg"
import { formatCurrency } from "../../utils/utils"
import styles from "./SafetyNetPaywall.module.scss"

const TrashIcon = svg.featureIcons.Trash
const CloudIcon = svg.featureIcons.CloudVault

/**
 * SafetyNetPaywall — an original, self-contained FEATURE-SPECIFIC paywall for the
 * real "Recently Deleted / Trash Recovery" Premium feature (deleted photos are
 * held for 30 days and can be restored in a tap).
 *
 * Divergence brief (claude-paywalls/_ledger.md; concept doc safety-net.md).
 * The four prior feature-specific concepts heroed break-in alerts (Caught, CCTV),
 * smart search (Recall, grid), the decoy passcode (Decoy, peel) and Face Down Lock
 * (Reflex, flip). None used deletion-recovery, and none used a physics mechanic.
 *
 *   - Layout:      a vertical DROP-SHAFT — a vault shelf up top, a fall gap, and a
 *                  stretched SAFETY NET slung between two posts at the bottom.
 *                  Not a hero+card / calendar / road / receipt / map / book /
 *                  chat / editor / device-stage / player / before-after / pager.
 *   - Interaction: TOSS-TO-DELETE + CATCH + FLING-BACK-TO-RESTORE. A real gravity
 *                  simulation: you throw a photo off the shelf, it falls, the net
 *                  dips and catches it with a bounce, a 30-day ring starts; tap a
 *                  caught photo and it flies home. First physics/toss-catch mechanic.
 *   - Register:    RELIEF / permission-to-let-go, lightly playful (the bouncy
 *                  catch) — not fear (Caught/Deep Freeze), luxury, nostalgia or speed.
 *   - Art:         light + airy + PHOTOGRAPHIC (Unsplash roll: use). Real memory
 *                  photos are the things you throw and catch; brand blue = the net /
 *                  safety, amber = the falling / at-risk instant before the catch.
 *   - Value frame: RISK REVERSAL / a safety net for deletion — "delete boldly,
 *                  nothing's gone for 30 days." Untouched: Receipt was device-loss
 *                  backup, this is deletion-undo. Honest loss-aversion, no data-loss
 *                  or expiration claim, real savings arithmetic.
 *
 * Yearly is the featured, default plan and carries the largest price; monthly is
 * small and secondary. App Store compliant.
 */

const MEMORIES = [
  "/safetynet/mem1.jpg",
  "/safetynet/mem2.jpg",
  "/safetynet/mem3.jpg",
  "/safetynet/mem4.jpg",
  "/safetynet/mem5.jpg",
  "/safetynet/mem6.jpg",
  "/safetynet/mem7.jpg",
  "/safetynet/mem8.jpg",
]

const CARD = 72 // px, the photo card size
const MAX_CAUGHT = 5
const GRAVITY = 2600 // px/s^2
const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v))

type PlanKey = "yearly" | "monthly"
type Caught = { id: number; img: string }
type Falling = { id: number; img: string }

const SafetyNetPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.round((1 - y / (m * 12)) * 100)
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<PlanKey>("yearly")

  // ── The drop-shaft mechanic ───────────────────────────────────────────────
  const idRef = useRef(1)
  const nextImg = useRef(0)
  const takeImg = useCallback(() => {
    const img = MEMORIES[nextImg.current % MEMORIES.length]
    nextImg.current += 1
    return img
  }, [])

  const [active, setActive] = useState<Caught>(() => ({
    id: 0,
    img: MEMORIES[0],
  }))
  const [falling, setFalling] = useState<Falling | null>(null)
  const [caught, setCaught] = useState<Caught[]>([])
  const [handedOver, setHandedOver] = useState(false)
  const [sag, setSag] = useState(0) // net dip in px
  const [flash, setFlash] = useState<"caught" | "restored" | null>(null)

  const stageRef = useRef<HTMLDivElement>(null)
  const fallElRef = useRef<HTMLDivElement>(null)
  const activeElRef = useRef<HTMLButtonElement>(null)
  const dimsRef = useRef({ w: 340, h: 340 })
  const [dims, setDims] = useState({ w: 340, h: 340 })
  const rafRef = useRef<number | null>(null)
  const sagAnim = useRef<ReturnType<typeof animate> | null>(null)
  const flashT = useRef<number | null>(null)

  // pointer-drag bookkeeping for the active (shelf) photo
  const drag = useRef({
    down: false,
    startX: 0,
    startY: 0,
    x: 0,
    y: 0,
    lastX: 0,
    lastY: 0,
    lastT: 0,
    vx: 0,
    vy: 0,
  })

  const measure = useCallback(() => {
    const el = stageRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const next = { w: r.width, h: r.height }
    dimsRef.current = next
    setDims(next)
  }, [])

  useEffect(() => {
    measure()
    window.addEventListener("resize", measure)
    return () => window.removeEventListener("resize", measure)
  }, [measure])

  // geometry helpers (px within the stage box)
  const shelfX = () => dimsRef.current.w / 2 - CARD / 2
  const shelfY = 18
  const netTop = () => dimsRef.current.h - 128 // resting top edge of the net
  // Lay the caught photos out as a group centred over the belly of the net, so
  // one rests dead-centre and more fan out symmetrically as the net fills.
  const slotX = (i: number, count: number) => {
    const w = dimsRef.current.w
    const n = Math.max(1, count)
    const usable = w - 52 - CARD
    const gap = n > 1 ? clamp(usable / (n - 1), 58, 86) : 0
    const totalW = gap * (n - 1) + CARD
    const startX = (w - totalW) / 2
    return startX + gap * i
  }

  const flashMsg = useCallback((k: "caught" | "restored") => {
    setFlash(k)
    if (flashT.current) window.clearTimeout(flashT.current)
    flashT.current = window.setTimeout(() => setFlash(null), 1400)
  }, [])

  // net catch dip → spring back
  const dipNet = useCallback(() => {
    sagAnim.current?.stop()
    if (reduceMotion) {
      setSag(0)
      return
    }
    sagAnim.current = animate(26, 0, {
      duration: 0.7,
      ease: [0.22, 1.4, 0.4, 1],
      onUpdate: (v) => setSag(v),
    })
  }, [reduceMotion])

  // Drop a photo from a starting point + velocity into the net.
  const dropFrom = useCallback(
    (img: string, id: number, x0: number, y0: number, vx = 0, vy = 0) => {
      setFalling({ img, id })
      const restY = netTop() - CARD * 0.34 // settles cradled in the net
      if (reduceMotion) {
        // no physics: place straight into the net
        const el = fallElRef.current
        if (el) el.style.transform = `translate(${slotX(0, 1)}px, ${restY}px)`
        window.setTimeout(() => {
          setCaught((c) => [...c, { id, img }].slice(-MAX_CAUGHT))
          setFalling(null)
          flashMsg("caught")
        }, 30)
        return
      }
      let x = x0
      let y = y0
      let vX = vx
      let vY = Math.max(vy, 120)
      let rot = 0
      const spin = clamp(vx * 0.04, -220, 220)
      let last =
        typeof performance !== "undefined" ? performance.now() : Date.now()
      const step = (now: number) => {
        const dt = Math.min(0.032, (now - last) / 1000)
        last = now
        vY += GRAVITY * dt
        x += vX * dt
        y += vY * dt
        rot += spin * dt
        const w = dimsRef.current.w
        // gentle walls so a hard sideways flick can't launch it off-stage
        if (x < 6) {
          x = 6
          vX = Math.abs(vX) * 0.5
        }
        if (x > w - CARD - 6) {
          x = w - CARD - 6
          vX = -Math.abs(vX) * 0.5
        }
        const el = fallElRef.current
        if (el)
          el.style.transform = `translate(${x}px, ${y}px) rotate(${rot}deg)`
        if (y >= restY) {
          setSag(26)
          dipNet()
          setCaught((c) => [...c, { id, img }].slice(-MAX_CAUGHT))
          setFalling(null)
          flashMsg("caught")
          rafRef.current = null
          return
        }
        rafRef.current = requestAnimationFrame(step)
      }
      rafRef.current = requestAnimationFrame(step)
    },
    [reduceMotion, dipNet, flashMsg],
  )

  // throw the current shelf photo away
  const throwActive = useCallback(
    (vx: number, vy: number, fromX: number, fromY: number) => {
      if (caught.length >= MAX_CAUGHT) return
      setHandedOver(true)
      const { id, img } = active
      const newId = idRef.current++
      // replace the shelf photo with the next memory
      setActive({ id: newId, img: takeImg() })
      dropFrom(img, id, fromX, fromY, vx, vy)
    },
    [active, caught.length, dropFrom, takeImg],
  )

  // restore a caught photo — it flies back up and lands on the shelf
  const restore = useCallback(
    (item: Caught) => {
      setHandedOver(true)
      setCaught((c) => c.filter((p) => p.id !== item.id))
      setActive({ id: idRef.current++, img: item.img })
      flashMsg("restored")
      const el = activeElRef.current
      if (el && !reduceMotion) {
        el.animate(
          [
            { transform: "translateY(46px) scale(0.82)", opacity: 0.4 },
            { transform: "translateY(0) scale(1)", opacity: 1 },
          ],
          { duration: 460, easing: "cubic-bezier(0.22,1.3,0.4,1)" },
        )
      }
    },
    [flashMsg, reduceMotion],
  )

  // ── active-photo pointer gestures ─────────────────────────────────────────
  const onDown = useCallback((e: React.PointerEvent) => {
    if (falling) return
    e.currentTarget.setPointerCapture(e.pointerId)
    const now =
      typeof performance !== "undefined" ? performance.now() : Date.now()
    drag.current = {
      down: true,
      startX: e.clientX,
      startY: e.clientY,
      x: 0,
      y: 0,
      lastX: e.clientX,
      lastY: e.clientY,
      lastT: now,
      vx: 0,
      vy: 0,
    }
  }, [])

  const onMove = useCallback((e: React.PointerEvent) => {
    const d = drag.current
    if (!d.down) return
    const now =
      typeof performance !== "undefined" ? performance.now() : Date.now()
    d.x = e.clientX - d.startX
    d.y = e.clientY - d.startY
    const dt = Math.max(0.001, (now - d.lastT) / 1000)
    d.vx = (e.clientX - d.lastX) / dt
    d.vy = (e.clientY - d.lastY) / dt
    d.lastX = e.clientX
    d.lastY = e.clientY
    d.lastT = now
    const el = activeElRef.current
    if (el)
      el.style.transform = `translate(${d.x}px, ${d.y}px) rotate(${clamp(
        d.x * 0.05,
        -12,
        12,
      )}deg)`
  }, [])

  const releaseThrow = useCallback(() => {
    const d = drag.current
    d.down = false
    const el = activeElRef.current
    // thrown if dragged down enough or flicked downward
    const thrown = d.y > 46 || d.vy > 320
    if (thrown && caught.length < MAX_CAUGHT) {
      if (el) el.style.transform = ""
      throwActive(d.vx, Math.max(d.vy, 140), shelfX() + d.x, shelfY + d.y)
    } else if (el) {
      // spring back to the shelf
      el.style.transition = "transform 0.4s cubic-bezier(0.22,1.4,0.4,1)"
      el.style.transform = ""
      window.setTimeout(() => {
        if (el) el.style.transition = ""
      }, 420)
    }
  }, [caught.length, throwActive])

  const onUp = useCallback(() => {
    if (drag.current.down) releaseThrow()
  }, [releaseThrow])

  // tap-to-delete fallback (accessibility / no-drag)
  const tapThrow = useCallback(() => {
    if (falling || caught.length >= MAX_CAUGHT) return
    if (Math.abs(drag.current.x) > 6 || Math.abs(drag.current.y) > 6) return
    throwActive(60, 260, shelfX(), shelfY)
  }, [falling, caught.length, throwActive])

  // Auto-demo once: lift, throw one photo, let the net catch it, hand it over.
  useEffect(() => {
    if (reduceMotion) {
      // show one already resting in the net so the concept still reads
      const img = takeImg()
      setCaught([{ id: idRef.current++, img }])
      setHandedOver(true)
      return
    }
    const t = window.setTimeout(() => {
      measure()
      const { id, img } = active
      setActive({ id: idRef.current++, img: takeImg() })
      dropFrom(img, id, shelfX(), shelfY + 4, 30, 150)
    }, 900)
    return () => {
      window.clearTimeout(t)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      sagAnim.current?.stop()
      if (flashT.current) window.clearTimeout(flashT.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const netFull = caught.length >= MAX_CAUGHT

  const handleClose = () => native.postNativeEvent("close")
  const handleSubscribe = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  // Net geometry (SVG path drawn in stage-pixel space, sag-animated).
  const W = dims.w
  const H = dims.h
  const netY = H - 128
  const p0 = { x: 20, y: netY }
  const p1 = { x: W - 20, y: netY }
  const dip = netY + 30 + sag
  const netPath = `M ${p0.x} ${p0.y} Q ${W / 2} ${dip} ${p1.x} ${p1.y}`
  const netBelly = `M ${p0.x} ${p0.y} Q ${W / 2} ${dip} ${p1.x} ${p1.y} L ${p1.x} ${
    netY + 6
  } Q ${W / 2} ${dip + 22} ${p0.x} ${netY + 6} Z`
  // vertical mesh strands
  const strands = Array.from({ length: 11 }, (_, i) => {
    const t = i / 10
    const x = p0.x + (p1.x - p0.x) * t
    const yTop = netY + Math.sin(Math.PI * t) * 3
    const yBot = dip * (1 - Math.abs(t - 0.5) * 0.9)
    const y = netY + (dip - netY) * Math.sin(Math.PI * t)
    return { x, yTop, y: Math.max(yTop, y), yBot }
  })

  return (
    <div className={styles.root}>
      <div className={styles.sky} aria-hidden="true" />

      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <path
                d="M9.2 12.1l2 2 3.6-3.9"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className={styles.brandText}>
            Private Photo Vault
            <em>Premium</em>
          </span>
        </div>
        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
            <path
              d="M5 5l14 14M19 5L5 19"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      <div className={styles.intro}>
        <span className={styles.badge}>
          <TrashIcon />
          Recently Deleted · Premium
        </span>
        <h1 className={styles.title}>
          Delete boldly.
          <br />
          <span className={styles.titleAccent}>
            Nothing's gone for 30 days.
          </span>
        </h1>
        <p className={styles.sub}>
          Every photo you delete drops into <b>Recently Deleted</b> and is held
          safe for 30 days. Clear out your vault without ever losing the one
          that mattered — bring anything back in a single tap.
        </p>
      </div>

      {/* ── THE DROP-SHAFT: throw a memory off the shelf, watch the net catch it ── */}
      <div className={styles.stageWrap}>
        <div className={styles.stage} ref={stageRef}>
          {/* the vault shelf */}
          <div className={styles.shelf} aria-hidden="true">
            <span className={styles.shelfLabel}>
              <CloudIcon />
              My Vault
            </span>
            <span className={styles.shelfLine} />
          </div>

          {/* the active photo you throw away */}
          {!falling && (
            <button
              ref={activeElRef}
              className={`${styles.card} ${styles.activeCard} ${
                netFull ? styles.cardLocked : ""
              }`}
              style={{ left: shelfX(), top: shelfY }}
              onPointerDown={onDown}
              onPointerMove={onMove}
              onPointerUp={onUp}
              onPointerCancel={onUp}
              onClick={tapThrow}
              aria-label={
                netFull
                  ? "Recently Deleted is full — restore a photo first"
                  : "Throw this photo away to delete it"
              }
            >
              <img src={active.img} alt="" draggable={false} />
              <span className={styles.cardGrab} />
            </button>
          )}

          {/* the falling photo (transform driven imperatively) */}
          {falling && (
            <div
              ref={fallElRef}
              className={`${styles.card} ${styles.fallCard}`}
              style={{ left: 0, top: 0 }}
              aria-hidden="true"
            >
              <img src={falling.img} alt="" draggable={false} />
            </div>
          )}

          {/* caught photos resting in the net */}
          {caught.map((item, i) => (
            <button
              key={item.id}
              className={`${styles.card} ${styles.caughtCard}`}
              style={{
                left: slotX(i, caught.length),
                top: netTop() - CARD * 0.34,
              }}
              onClick={() => restore(item)}
              aria-label="Restore this photo to your vault"
            >
              <img src={item.img} alt="" draggable={false} />
              <span className={styles.ring} aria-hidden="true">
                <svg viewBox="0 0 40 40" width="40" height="40">
                  <circle
                    cx="20"
                    cy="20"
                    r="18"
                    fill="none"
                    stroke="rgba(43,95,245,0.18)"
                    strokeWidth="3"
                  />
                  <circle
                    cx="20"
                    cy="20"
                    r="18"
                    fill="none"
                    stroke="var(--color-primary)"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeDasharray="113"
                    strokeDashoffset="9"
                    transform="rotate(-90 20 20)"
                  />
                </svg>
                <span className={styles.ringText}>30d</span>
              </span>
              <span className={styles.restoreHint}>
                <svg viewBox="0 0 24 24" width="11" height="11">
                  <path
                    d="M12 19V7M6 11l6-6 6 6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                restore
              </span>
            </button>
          ))}

          {/* the safety net */}
          <svg
            className={styles.net}
            width={W}
            height={H}
            viewBox={`0 0 ${W} ${H}`}
            aria-hidden="true"
          >
            <path d={netBelly} fill="rgba(43,95,245,0.07)" />
            {strands.map((s, i) => (
              <line
                key={i}
                x1={s.x}
                y1={s.yTop}
                x2={s.x}
                y2={s.y}
                stroke="rgba(43,95,245,0.28)"
                strokeWidth="1.4"
              />
            ))}
            <path
              d={netPath}
              fill="none"
              stroke="var(--color-primary)"
              strokeWidth="3"
              strokeLinecap="round"
            />
            <path
              d={`M ${p0.x} ${netY + 6} Q ${W / 2} ${dip + 22} ${p1.x} ${
                netY + 6
              }`}
              fill="none"
              stroke="rgba(43,95,245,0.4)"
              strokeWidth="2"
              strokeLinecap="round"
            />
            {/* posts */}
            <rect
              x={p0.x - 5}
              y={netY - 4}
              width="6"
              height="34"
              rx="3"
              fill="var(--color-primary)"
            />
            <rect
              x={p1.x - 1}
              y={netY - 4}
              width="6"
              height="34"
              rx="3"
              fill="var(--color-primary)"
            />
          </svg>

          {/* net caption + live status */}
          <div className={styles.netTag} aria-hidden="true">
            <span className={styles.netDot} />
            Recently Deleted · held 30 days
          </div>

          <div
            className={`${styles.flash} ${flash ? styles.flashOn : ""}`}
            aria-live="polite"
          >
            {flash === "caught"
              ? "Caught — recoverable for 30 days"
              : flash === "restored"
                ? "Restored to your vault"
                : ""}
          </div>

          {/* throw hint */}
          {handedOver && !falling && !netFull && (
            <div className={styles.hint} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="13" height="13">
                <path
                  d="M12 5v14M6 13l6 6 6-6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              throw it away — the net's got it
            </div>
          )}
          {netFull && (
            <div className={styles.hint} aria-hidden="true">
              tap any photo in the net to bring it back
            </div>
          )}
        </div>

        <p className={styles.stageCaption}>
          Try it. Toss a memory off the shelf — Recently Deleted catches
          everything, so a mistap is never the end.
        </p>
      </div>

      {/* small reassurance strip — specific to recovery, not a generic perk list */}
      <div className={styles.assure}>
        <span className={styles.assureItem}>
          <b>1-tap</b>
          restore
        </span>
        <span className={styles.assureDiv} />
        <span className={styles.assureItem}>
          <b>Full-res</b>
          exactly as shot
        </span>
        <span className={styles.assureDiv} />
        <span className={styles.assureItem}>
          <b>Albums too</b>
          not just photos
        </span>
      </div>

      {/* ── PRICING: yearly featured ─────────────────────────────────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.yearly} ${plan === "yearly" ? styles.selected : ""}`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.yearlyGlow} aria-hidden="true" />
          <span className={styles.yearlyTop}>
            <span className={styles.yearlyName}>
              Yearly
              {savingsPct > 0 && (
                <span className={styles.saveTag}>Save {savingsPct}%</span>
              )}
            </span>
            <span className={styles.check} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="13" height="13">
                <path
                  d="M5 12.5l4.2 4.2L19 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </span>
          <span className={styles.yearlyPriceRow}>
            <span className={styles.yearlyPrice}>{yearlyPrice}</span>
            <span className={styles.yearlyUnit}>/ year</span>
          </span>
          <span className={styles.yearlyPerMo}>
            just {yearlyPerMonth}/mo, billed annually
          </span>
        </button>

        <button
          className={`${styles.monthly} ${plan === "monthly" ? styles.monthlySel : ""}`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.monthlyName}>Monthly</span>
          <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <div className={styles.footerLinks}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>

      <div className={styles.dock}>
        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaMain}>
            {plan === "yearly"
              ? "Keep the safety net on"
              : "Turn on Recently Deleted"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? hasTrial
                ? `7-day free trial, then ${yearlyPrice}/yr · cancel anytime`
                : `${yearlyPrice}/yr · cancel anytime`
              : `${monthlyPrice}/mo · cancel anytime`}
          </span>
        </button>
      </div>
    </div>
  )
}

export default SafetyNetPaywall
